import { useI18n } from '@ecom/i18n';
import { type ReactElement } from 'react';

import type { UseActiveShopResult } from '@/features/shops/use-active-shop';

/**
 * `useActiveShop()` is called ONCE, in AppShell, and passed down here as a
 * prop — the identical lesson Lot 1 learned the hard way with `useTheme()`
 * (two independent instances desynced live; see AppShell's own comment).
 * A second instance here would refetch `/auth/me/memberships` redundantly
 * and risk the same class of desync between what this switcher shows and
 * what the rest of the app has bound.
 */
export function ShopSwitcher({ shop }: { shop: UseActiveShopResult }): ReactElement {
  const { t } = useI18n();

  if (shop.isLoading) {
    return <div className="ecom-skeleton h-9 w-48 rounded-md" aria-hidden="true" />;
  }

  if (shop.memberships.length === 0) {
    return <span className="text-sm text-inkFaint">{t('merchant.shopSwitcher.none')}</span>;
  }

  if (shop.memberships.length === 1) {
    const only = shop.memberships[0];
    return (
      <span className="truncate text-sm font-medium text-ink" title={only?.legalName ?? undefined}>
        {only?.legalName ?? t('merchant.shopSwitcher.unnamed')}
      </span>
    );
  }

  return (
    <select
      value={shop.activeShopId ?? ''}
      onChange={(event) => {
        shop.selectShop(event.target.value);
      }}
      aria-label={t('merchant.shopSwitcher.activeAria')}
      className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-ink"
    >
      {shop.memberships.map((membership) => (
        <option
          key={membership.tenantId}
          value={membership.tenantId}
          disabled={membership.membershipStatus !== 'active'}
        >
          {membership.legalName ?? membership.tenantId}
          {membership.membershipStatus !== 'active'
            ? ` (${t('merchant.shopSwitcher.suspended')})`
            : ''}
        </option>
      ))}
    </select>
  );
}
